import React from "react";
import { Trans } from "@lingui/macro";
import { validateEmail } from "../../../../../utils";
import { Alert } from "../../../../../UI/Alert/Alert";
import { useDispatch, useSelector } from "react-redux";
import { Loader } from "../../../../../UI/Loader/Loader";
import { Button } from "../../../../../UI/Button/Button";
import { ExclamationCircleIcon } from "@heroicons/react/outline";
import { nullAlias } from "../../../../../../constants/nullAlias";
import { InputText } from "../../../../../UI/InputText/InputText";
import { InputEmail } from "../../../../../UI/InputEmail/InputEmail";
import { isValidPhone } from "../../../../../../utils/is-valid-phone";
import { startNumber } from "../../../../../../model/system/constants";
import { useInputChange } from "../../../../../../hooks/useInputChange";
import { USERPOSITIONS } from "../../../../../../constants/userPositions";
import { InputTextarea } from "../../../../../UI/InputTextarea/InputTextarea";
import { NULL_VALUE, SelectBox } from "../../../../../UI/SelectBox/SelectBox";
import { getUsersList } from "../../../../../../model/dashboardUsers/dUsersAPI";
import { changeStoresItem } from "../../../../../../model/dashboardStores/dStoresAPI";
import { InputMaskedPhone } from "../../../../../UI/InputMaskedPhone/InputMaskedPhone";
import { IdUsersStateDataItem } from "../../../../../../model/dashboardUsers/dUsersReducer";
import {
  logoutSagaAC,
  addNotificationAC,
} from "../../../../../../model/system/systemActionCreators";
import {
  IdStoresState,
  typeEditStore,
} from "../../../../../../model/dashboardStores/dStoresReducer";
import {
  dStoresSetDataSagaAC,
  dStoresSetDetailItemAC,
} from "../../../../../../model/dashboardStores/dStoresActionCreators";

export type typePopupEdit = {
  setOpenPopup: React.Dispatch<React.SetStateAction<boolean>>;
};

export const PopupEdit: React.FC<typePopupEdit> = ({ setOpenPopup }) => {
  const dispatch = useDispatch();

  const filters = useSelector(
    (state: { dStores: IdStoresState }) => state.dStores.filters,
  );
  const pagination = useSelector(
    (state: { dStores: IdStoresState }) => state.dStores.pagination,
  );
  const dataObject = useSelector(
    (state: { dStores: IdStoresState }) => state.dStores.detailItem,
  );

  const { value: nameValue, changeValueHandler: nameChangeValueHandler } =
    useInputChange(dataObject && dataObject.name ? dataObject.name : "");
  const { value: phoneValue, setValue: phoneChangeValueHandler } =
    useInputChange(
      dataObject && dataObject.phoneNumber ? dataObject.phoneNumber : "",
    );
  const { value: emailValue, changeValueHandler: emailChangeValueHandler } =
    useInputChange(dataObject && dataObject.email ? dataObject.email : "");
  const {
    value: managerPhoneValue,
    setValue: setManagerPhoneValue,
    changeValueHandler: managerPhoneChangeValueHandler,
  } = useInputChange(
    dataObject && dataObject.managerPhoneNumber
      ? dataObject.managerPhoneNumber
      : "",
  );
  const { value: commentValue, changeValueHandler: commentChangeValueHandler } =
    useInputChange(
      dataObject && dataObject.description ? dataObject.description : "",
    );

  const [managerIdValue, setManagerIdValue] = React.useState<string | null>(
    NULL_VALUE.id,
  );
  const [selectManagerList, setSelectManagerList] = React.useState<
    IdUsersStateDataItem[]
  >([]);

  const [loader, setLoader] = React.useState(false);
  const [loaderManagerList, setLoaderManagerList] = React.useState(false);
  const [nonExistentManager, setNonExistentManager] = React.useState(false);
  const [validationObj, setValidationObj] = React.useState({
    uniqueName: true,
    validEmail: true,
    validPhone: true,
    requiredName: true,
  });

  React.useEffect(() => {
    return () => {
      dispatch(dStoresSetDetailItemAC(undefined));
    };
  }, [dispatch]);

  React.useEffect(() => {
    if (nameValue.trim() === "") {
      setValidationObj((v) => {
        return { ...v, ...{ requiredName: false, uniqueName: true } };
      });
    } else {
      setValidationObj((v) => {
        return { ...v, ...{ requiredName: true, uniqueName: true } };
      });
    }
  }, [nameValue]);

  React.useEffect(() => {
    if (dataObject) {
      setLoaderManagerList(true);

      const setData = async () => {
        let responseManagers = await getUsersList({
          filters: { deleted: false },
          full: false,
        });

        if (responseManagers.status === 200) {
          const newManagersList = responseManagers.data.filter(
            (item: IdUsersStateDataItem) =>
              (item.position === USERPOSITIONS.MANAGER ||
                item.position === USERPOSITIONS.ADMIN) &&
              item.storeId === dataObject.id,
          );
          setSelectManagerList(newManagersList);
        } else if (responseManagers.status === 401) {
          dispatch(logoutSagaAC());
        }

        setLoaderManagerList(false);
      };

      setData().catch(console.error);
    }

    // eslint-disable-next-line
  }, [dataObject]);

  React.useEffect(() => {
    if (phoneValue.trim() === "") {
      setValidationObj((v) => {
        return { ...v, ...{ validPhone: true } };
      });
    } else if (!isValidPhone(phoneValue)) {
      if (phoneValue === startNumber) {
        setValidationObj((v) => {
          return { ...v, ...{ validPhone: true } };
        });
      } else {
        setValidationObj((v) => {
          return { ...v, ...{ validPhone: false } };
        });
      }
    } else {
      setValidationObj((v) => {
        return { ...v, ...{ validPhone: true } };
      });
    }
  }, [phoneValue]);

  React.useEffect(() => {
    if (emailValue.trim() !== "") {
      if (validateEmail(emailValue.trim())) {
        setValidationObj((v) => {
          return { ...v, ...{ validEmail: true } };
        });
      } else {
        setValidationObj((v) => {
          return { ...v, ...{ validEmail: false } };
        });
      }
    } else {
      setValidationObj((v) => {
        return { ...v, ...{ validEmail: true } };
      });
    }
  }, [emailValue]);

  React.useEffect(() => {
    if (managerIdValue === NULL_VALUE.id || managerIdValue === null) {
      setManagerPhoneValue("");
    } else {
      // @ts-ignore
      const phone = selectManagerList!.find(
        (item: IdUsersStateDataItem) => item.id === managerIdValue,
      ).phoneNumber;

      setManagerPhoneValue(phone === null ? "" : phone);
    }
    // eslint-disable-next-line
  }, [managerIdValue]);

  React.useEffect(() => {
    if (!!selectManagerList.length && dataObject?.manager !== "") {
      const managerId = selectManagerList.find(
        (item: IdUsersStateDataItem) => item.name === dataObject?.manager,
      )?.id;

      if (managerId) {
        setManagerIdValue(managerId);

        setNonExistentManager(false);
      } else {
        setNonExistentManager(true);
      }
    }

    // eslint-disable-next-line
  }, [selectManagerList]);

  const onSaveHandler = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoader(true);

    const managerData: IdUsersStateDataItem | undefined =
      selectManagerList.find((item) => item.id === managerIdValue);

    const dataEditObject = {
      name: nameValue.trim(),
      phoneNumber:
        phoneValue.trim() === ""
          ? null
          : phoneValue.trim().length > 5
            ? phoneValue.trim()
            : null,
      email: !!emailValue.trim() ? emailValue.trim() : null,
      manager: managerData ? managerData.name : null,
      managerPhoneNumber: !!managerPhoneValue.trim()
        ? managerPhoneValue.trim()
        : null,
      description: !!commentValue.trim() ? commentValue.trim() : null,
    };

    const newData: typeEditStore = {
      id: dataObject!.id,
    };

    Object.entries(dataEditObject).forEach((item) => {
      if (
        dataObject![item[0] as keyof typeof dataEditObject] !==
        dataEditObject[item[0] as keyof typeof dataEditObject]
      ) {
        newData[item[0] as keyof typeof dataEditObject] =
          item[1] === null ? nullAlias : item[1];
      }
    });

    const response = await changeStoresItem({ dataObject: newData });

    if (response.status === 200) {
      const id = new Date();
      dispatch(
        addNotificationAC({
          id: id.getTime().toString(),
          message: "SUCCESS_CHANGE_ITEM",
        }),
      );

      dispatch(
        dStoresSetDataSagaAC(filters, {
          page: pagination.pageNumber,
          size: pagination.itemsPerPage!,
        }),
      );

      setOpenPopup(false);
    } else if (response.status === 400) {
      const id = new Date();

      switch (response.data.errorCode) {
        case "validation.store.nameExist":
          setValidationObj({ ...validationObj, ...{ uniqueName: false } });

          break;
        default:
          dispatch(
            addNotificationAC({
              id: id.getTime().toString(),
              message: "ERROR_EDIT_STORE_FIELD_VALIDATION",
            }),
          );
      }
    } else {
      const id = new Date();
      dispatch(
        addNotificationAC({
          id: id.getTime().toString(),
          message: "ERROR_EDIT_STORE_UNKNOWN",
        }),
      );
    }

    setLoader(false);
  };

  const disabled =
    !validationObj.uniqueName ||
    !validationObj.validEmail ||
    !validationObj.validPhone ||
    !validationObj.requiredName ||
    loader ||
    loaderManagerList;

  return dataObject ? (
    <form>
      <input type="hidden" value={dataObject.id} />

      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <InputText
            id="name"
            name="name"
            required={true}
            maxLength={100}
            value={nameValue}
            labelClassName="text-left"
            onChange={nameChangeValueHandler}
            label={<Trans>Store name</Trans>}
            errorMessage={
              validationObj.requiredName ? (
                validationObj.uniqueName ? undefined : (
                  <Trans>This name already exists</Trans>
                )
              ) : (
                <Trans>Required field</Trans>
              )
            }
          />
        </div>

        <div>
          <InputMaskedPhone
            id="phone"
            name="phone"
            maxLength={25}
            value={phoneValue}
            labelClassName="text-left"
            label={<Trans>Phone</Trans>}
            setValue={phoneChangeValueHandler}
            errorMessage={
              validationObj.validPhone ? undefined : (
                <Trans>Enter valid phone number</Trans>
              )
            }
          />
        </div>

        <div>
          <InputEmail
            id="email"
            name="email"
            maxLength={50}
            value={emailValue}
            labelClassName="text-left"
            label={<Trans>Email</Trans>}
            onChange={emailChangeValueHandler}
            errorMessage={
              validationObj.validEmail ? undefined : (
                <Trans>Enter valid email</Trans>
              )
            }
          />
        </div>
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        <div className="w-72 relative">
          <SelectBox
            valueId={managerIdValue}
            labelClassname="text-left"
            label={<Trans>Manager</Trans>}
            valuesList={selectManagerList}
            setIdValue={setManagerIdValue}
          />
          {selectManagerList.length === 0 && !loaderManagerList && (
            <div className="flex mt-1">
              <ExclamationCircleIcon className="w-4 h-4 mr-1 text-gray-600" />
              <p className="text-xs text-gray-500 text-center">
                <Trans>You have no active managers</Trans>
              </p>
            </div>
          )}
        </div>

        <div>
          <InputText
            maxLength={25}
            disabled={true}
            id="managerPhone"
            name="managerPhone"
            value={managerPhoneValue}
            labelClassName="text-left"
            inputClassName="bg-gray-50"
            onChange={managerPhoneChangeValueHandler}
            label={<Trans>Manager Phone</Trans>}
          />
        </div>

        {nonExistentManager && !!dataObject.manager && (
          <div className="flex sm:col-span-2">
            <ExclamationCircleIcon className="w-4 h-4 mr-1 text-red-400" />
            <p className="text-xs text-gray-500 flex-grow">
              <Trans>
                This manager <b className="uppercase">{dataObject.manager}</b>{" "}
                doesn't exist in the list of current managers.
              </Trans>
            </p>
          </div>
        )}
      </div>

      <div className="mt-8">
        <div>
          <InputTextarea
            id="comment"
            name="comment"
            maxLength={150}
            value={commentValue}
            labelClassName="text-left"
            label={<Trans>Description</Trans>}
            onChange={commentChangeValueHandler}
          />
        </div>
      </div>

      <div className="flex gap-4 mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <Button
          type="submit"
          disabled={disabled}
          onClick={onSaveHandler}
          label={<Trans>Save</Trans>}
        />
        <Button
          type="button"
          secondary={true}
          label={<Trans>Cancel</Trans>}
          onClick={() => setOpenPopup(false)}
        />
      </div>

      {loader && <Loader />}
    </form>
  ) : (
    <Alert type="error" title={<Trans>Have no data.</Trans>} />
  );
};
